import React from 'react';
import tw from 'twin.macro';
import { Intent } from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import getIconName from './getIconName';
import styled from '@emotion/styled';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '@teetsh/app/src/components/Buttons/Button';

export type InfoboxLayout = 'inline' | 'block';
interface Props {
  title?: any;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  intent?: Intent | 'primary_intense'; // 'primary_intense' is useful on brand-quiet background
  layout?: InfoboxLayout;
  icon?: any;
  className?: string;
  onClose?: () => void;
  large?: boolean;
  'data-testid'?: string;
}

const cardColorIntent = {
  primary_intense: tw`bg-brand-moderate border-brand-moderate`,
  primary: tw`bg-brand-quiet border-brand-moderate`,
  danger: tw`bg-danger-quiet border-danger-moderate`,
  warning: tw`bg-warning-quiet border-warning-moderate`,
  success: tw`bg-positive-quiet border-positive-moderate`,
  none: tw`bg-neutral-supershy border-neutral-intense`,
};

const titleColorIntent = {
  primary: tw`text-brand-intense`,
  danger: tw`text-danger-intense`,
  warning: tw`text-warning-intense`,
  success: tw`text-positive-intense`,
  none: tw`text-neutral-intense`,
};

const textColorIntent = {
  primary: tw`text-brand-moderate`,
  danger: tw`text-danger-moderate`,
  warning: tw`text-warning-moderate`,
  success: tw`text-positive-moderate`,
  none: tw`text-neutral-moderate`,
};

const iconColorIntent = {
  primary: tw`text-brand-intense`,
  danger: tw`text-danger-intense`,
  warning: tw`text-warning-intense`,
  success: tw`text-positive-intense`,
  none: tw`text-neutral-moderate`,
};

const StyledIcons: any = styled.div`
  ${tw`h-6 w-6`}
  ${(p: any) => iconColorIntent[p.intent]}
  ${tw`shrink-0 mr-3`}
`;

export function Infobox(props: Props) {
  const {
    title,
    children,
    actions,
    intent,
    layout = 'block',
    large,
    icon,
    className,
    onClose,
  } = props;
  const mainIntent = intent === 'primary_intense' ? 'primary' : intent;
  const iconName = getIconName(icon, mainIntent);
  return (
    <div
      className={className}
      css={[tw`p-4 border rounded-lg`, cardColorIntent[intent || 'none']]}
      data-testid={props['data-testid']}
    >
      <div
        css={[
          tw`flex`,
          layout === 'inline' && !children && tw`md:items-center`,
        ]}
      >
        {iconName && (
          <div tw="shrink-0">
            <StyledIcons as={iconName as any} intent={mainIntent} />
          </div>
        )}
        <div
          css={[
            large ? tw`text-base` : tw`text-sm`,
            layout === 'inline' && tw`md:flex`,
          ]}
          tw="space-y-2 grow"
        >
          <div
            css={[tw`flex`, layout === 'inline' && tw`md:grow md:items-center`]}
          >
            <div tw="grow">
              {title && (
                <div>
                  <strong css={titleColorIntent[mainIntent || 'none']}>
                    {title}
                  </strong>
                  <br />
                </div>
              )}
              {children && (
                <div css={[textColorIntent[mainIntent || 'none']]}>
                  {children}
                </div>
              )}
            </div>
            {actions && layout === 'inline' && (
              <div tw="ml-4 hidden md:block">{actions}</div>
            )}
            {onClose && (
              <div tw="ml-auto pl-3">
                <div tw="-mx-1.5 -my-1.5">
                  <Button
                    minimal
                    intent={mainIntent}
                    aria-label="Fermer la popup"
                    tw="h-5 w-5"
                    onClick={() => {
                      onClose();
                    }}
                    icon={XMarkIcon}
                  />
                </div>
              </div>
            )}
          </div>
          {actions && (
            <div
              css={[
                tw`mt-3 flex justify-end`,
                layout === 'inline' && tw`md:hidden`,
              ]}
            >
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
